import { SeadevShedBuilder } from '../ssb_builder';
import { SHEDPRO_EXCLUDED_KEYS, DATA_MODEL_EXCLUDED_KEYS } from './constants';
import { omitKeys } from './utils';
import { getCurrentSizeRecord, getCurrentWainscotingRecord } from './size-record';
import {
	getPlacedComponentUuids,
	filterComponentsByCatalog,
	filterUpgradesBySelection,
	filterPackageBySelection,
	filterLoftBySelection,
	filterRunwayBySelection,
	filterCornerTrimBySelection,
	filterElectricalPackageBySelection,
	filterTackroomBySelection,
	filterFrameCatalog,
	filterPorchBySelection,
	filterMaterials,
} from './catalog-filters';
import { SnapshotPayload, GetListColorDataFn } from './types';

export interface BuildSnapshotPayloadOptions {
	rawFormData: any;
	shedBuilder: SeadevShedBuilder;
	getListColorData: GetListColorDataFn;
}

/**
 * Merges form data with global window state (model, colors, shedpro) into the final snapshot payload.
 *
 * @param options - Configuration containing raw form data, shed builder instance, and color data getter.
 * @returns Complete snapshot payload.
 */
export function buildSnapshotPayload(options: BuildSnapshotPayloadOptions): SnapshotPayload {
	const { rawFormData, shedBuilder, getListColorData } = options;
	const { shedpro, model } = window as any;

	const sanitizedShedpro = omitKeys(shedpro, SHEDPRO_EXCLUDED_KEYS);
	const sanitizedModel = omitKeys(model, DATA_MODEL_EXCLUDED_KEYS);

	const currentSizeRecord = getCurrentSizeRecord(sanitizedModel?.sizes);
	const currentWainscotingRecord = getCurrentWainscotingRecord(sanitizedModel?.wainscoting);

	// Collect placed components once; all three component-catalog filters reuse this set
	// instead of re-reading the canvas and rebuilding the uuid index three times.
	const placedUuids = getPlacedComponentUuids(shedBuilder.getAllComponents());

	const components = filterComponentsByCatalog(placedUuids, model?.componentCatalog);
	const iComponents = filterComponentsByCatalog(placedUuids, model?.iComponentCatalog);
	const electricsComponents = filterComponentsByCatalog(placedUuids, model?.electricsCatalog);

	const upgradesOtherCatalog = filterUpgradesBySelection(model?.upgradesOtherCatalog);
	const upgradesPackageCatalog = filterPackageBySelection(model?.upgradesPackageCatalog);
	const loftCatalog = filterLoftBySelection(model?.loftCatalog);
	const runwayOptionsCatalog = filterRunwayBySelection(model?.runwayOptionsCatalog);
	const cornerTrimCatalog = filterCornerTrimBySelection(model?.cornerTrimCatalog);
	const porchCatalog = filterPorchBySelection(model?.porchCatalog);
	const insulationWallMaterials = filterMaterials(
		model?.insulationWallMaterials,
		'insulation_wall-material-option',
	);
	const interiorWallMaterials = filterMaterials(
		model?.interiorWallMaterials,
		'interior_wall-material-option',
	);
	const interiorCeilingMaterials = filterMaterials(
		model?.interiorCeilingMaterials,
		'interior_ceiling-material-option',
	);
	const insulationRoofMaterials = filterMaterials(
		model?.insulationRoofMaterials,
		'insulation_roof-material-option',
	);
	const floorMaterials = filterMaterials(
		model?.floorMaterials,
		'floor-material-option',
		'data-floor-type',
		(item: any) => item?.material?.value,
	);

	const roofMaterials = filterMaterials(
		model?.roofMaterials,
		'roof-material-option',
		'data-roof-type',
		(item: any) => item?.ssb_configurator_material,
	);

	const sidingMaterials = filterMaterials(
		model?.sidingMaterials,
		'siding-material-option',
		'data-siding-type',
		(item: any) => item?.ssb_configurator_material?.value,
	);

	const electricalPackages = filterElectricalPackageBySelection(model?.electricalPackages);
	const tackroomItems = filterTackroomBySelection(model?.tackroomItems);
	const frameCatalog = filterFrameCatalog(model?.frameCatalog);

	const snapshotModel = {
		...sanitizedModel,
		sizes: currentSizeRecord ? [currentSizeRecord] : [],
		wainscoting: currentWainscotingRecord ? [currentWainscotingRecord] : [],
		componentCatalog: components,
		iComponentCatalog: iComponents,
		electricsCatalog: electricsComponents,
		upgradesOtherCatalog,
		upgradesPackageCatalog,
		loftCatalog,
		runwayOptionsCatalog,
		cornerTrimCatalog,
		porchCatalog,
		insulationWallMaterials,
		interiorWallMaterials,
		interiorCeilingMaterials,
		insulationRoofMaterials,
		floorMaterials,
		roofMaterials,
		sidingMaterials,
		electricalPackages,
		tackroomItems,
		frameCatalog,
	};

	return {
		...rawFormData,
		listColors: getListColorData(false),
		shedpro: sanitizedShedpro,
		dataModel: snapshotModel,
		dateCreate: Date.now().toString(),
	};
}
